import { Injectable } from '@angular/core';
import { ToasterPlacement } from '@coreui/angular';
import { Subject } from 'rxjs';
import { ToastOptions, ToastSeverity } from './toast.interface';

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly _toast$ = new Subject<ToastOptions>();
  readonly toast$ = this._toast$.asObservable();

  /**
   * Show a toast notification.
   * @param options The options for the toast notification.
   * @param options.message The message to show in the notification.
   * @param [options.severity] The severity of the notification (defaults to 'info').
   *   Possible values (from ToastSeverityEnum):
   *     - 'success' (ToastSeverityEnum.Success)
   *     - 'danger'  (ToastSeverityEnum.Danger)
   *     - 'info'    (ToastSeverityEnum.Info)
   *     - 'warning' (ToastSeverityEnum.Warning)
   * @param [options.timer] The time in milliseconds to show the notification for (defaults to null, which means the notification will be shown until the user closes it).
   * @param [options.position] The position of the notification (defaults to 'top-end').
   *   Possible values (from ToasterPlacement):
   *     - 'top'      (ToasterPlacement.Top)
   *     - 'top-end' (ToasterPlacement.TopEnd)
   *     - 'top-start' (ToasterPlacement.TopStart)
   *     - 'bottom'    (ToasterPlacement.Bottom)
   *     - 'bottom-end' (ToasterPlacement.BottomEnd)
   *     - 'bottom-start' (ToasterPlacement.BottomStart)
   */
  show(options: ToastOptions): void {
    this._toast$.next({
      autohide: true,
      delay: 5000,
      fade: true,
      closeButton: true,
      placement: ToasterPlacement.TopEnd,
      ...options,
    });
  }

  success(message: string, options: Omit<ToastOptions, 'message' | 'severity'> = {}): void {
    this.show({ message, severity: 'success', ...options });
  }

  info(message: string, options: Omit<ToastOptions, 'message' | 'severity'> = {}): void {
    this.show({ message, severity: 'info', ...options });
  }

  warning(message: string, options: Omit<ToastOptions, 'message' | 'severity'> = {}): void {
    this.show({ message, severity: 'warning', ...options });
  }

  danger(message: string, options: Omit<ToastOptions, 'message' | 'severity'> = {}): void {
    this.show({ message, severity: 'danger', ...options });
  }

  static severityToColor(severity?: ToastSeverity): string | undefined {
    switch (severity) {
      case 'success':
        return 'success';
      case 'info':
        return 'info';
      case 'warning':
        return 'warning';
      case 'danger':
        return 'danger';
      default:
        return undefined;
    }
  }

  static severityToIcon(severity?: ToastSeverity): string | undefined {
    switch (severity) {
      case 'success':
        return 'cil-check-circle';
      case 'info':
        return 'cil-info';
      case 'warning':
        return 'cil-warning';
      case 'danger':
        return 'cil-x-circle';
      default:
        return undefined;
    }
  }

  static severityToTitle(severity?: ToastSeverity): string | undefined {
    switch (severity) {
      case 'success':
        return 'Exito';
      case 'info':
        return 'Informacion';
      case 'warning':
        return 'Advertencia';
      case 'danger':
        return 'Error';
      default:
        return undefined;
    }
  }

  static colorToTextColor(color?: string): string | undefined {
    switch (color) {
      case 'light':
      case 'warning':
      case 'info':
        return 'dark';
      case 'dark':
        return 'light';
      default:
        return color ? 'light' : undefined;
    }
  }
}
